import React, {useEffect, useState, useRef, useMemo, useCallback} from 'react';
import {RouteComponentProps, Link} from 'react-router-dom';
import {
  Box,
  Typography,
  Breadcrumbs,
  Paper,
  Button,
  Snackbar,
} from '@material-ui/core';
import {Skeleton} from '@material-ui/lab';
import {connect} from 'react-redux';

import {
  ILayerResponse,
  IPolygonData,
  ISupplierUserProfile,
  IUser,
} from 'constants/interfaces';
// eslint-disable-next-line max-len
import {UpdateFieldForm} from 'screens/Dashboard/Customers/CustomerDetail/CustomerDashboardMapping/components/UpdateFieldForm';
// eslint-disable-next-line max-len
import {AddNewDataPointForm} from 'screens/Dashboard/Customers/CustomerDetail/CustomerDashboardMapping/components/AddNewDataPointForm';
import {
  apiDeleteFarmField,
  apiFieldLayersByIds,
  apiGetUser,
} from 'constants/Api';
import {
  GoogleMap,
  LoadScript,
  Polygon,
  OverlayView,
  Marker,
} from '@react-google-maps/api';
import {TerraDraw, TerraDrawPolygonMode, TerraDrawRenderMode} from 'terra-draw';
import {TerraDrawGoogleMapsAdapter} from 'terra-draw-google-maps-adapter';

import './CustomerDashboardMapping.scss';
import {GOOGLE_API_KEY} from 'constants/index';

import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
  faHandHoldingSeedling,
  faDroplet,
  faLayerGroup,
} from '@fortawesome/pro-regular-svg-icons';

// eslint-disable-next-line max-len
import LayerSelector from 'screens/Dashboard/Customers/CustomerDetail/CustomerDashboardMapping/components/LayerSelector';
import OverlayImage from 'screens/Dashboard/Customers/CustomerDetail/CustomerDashboardMapping/components/OverlayImage';
import {MuiPickersUtilsProvider, Calendar} from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import moment, {Moment} from 'moment';

import {FieldView} from './components/FieldView';
// eslint-disable-next-line max-len
import {AddNewFieldForm} from 'screens/Dashboard/Customers/CustomerDetail/CustomerDashboardMapping/components/AddNewFieldForm';
// eslint-disable-next-line max-len
import AddDataButtons from 'screens/Dashboard/Customers/CustomerDetail/CustomerDashboardMapping/components/AddDataButtons';

type DrawingModeType = 'POLYGON' | null;

interface MatchParams {
  customerUserId: string;
}

interface CustomerDashboardProps extends RouteComponentProps<MatchParams> {
  user: ISupplierUserProfile;
}
export enum EFarmLayer {
  NDVI = 'NDVI',
  MOISTURE = 'MOISTURE',
  CHLOROPHYLL = 'CHLOROPHYLL',
  RGB = 'RGB',
}
interface LayerOption {
  key: string;
  icon: React.ReactNode;
  label: string;
  valueItem: EFarmLayer;
}

// Parse lat/long from "lat,lng" string
function parseLatLong(latLong?: string) {
  if (!latLong) return {lat: -29.0, lng: 25.0};
  const parts = latLong.split(',');
  if (parts.length >= 2) {
    const lat = parseFloat(parts[0].trim());
    const lng = parseFloat(parts[1].trim());
    if (!isNaN(lat) && !isNaN(lng)) {
      return {lat, lng};
    }
  }
  return {lat: -29.0, lng: 25.0};
}

// Approximate polygon centroid by averaging lat/lng
function getPolygonCentroid(coords: {lat: number; lng: number}[]) {
  if (!coords?.length) return {lat: 0, lng: 0};
  let latSum = 0;
  let lngSum = 0;
  coords.forEach((pt) => {
    latSum += pt.lat;
    lngSum += pt.lng;
  });
  return {
    lat: latSum / coords.length,
    lng: lngSum / coords.length,
  };
}

function toIsoDayKey(rawDate: unknown): string | null {
  if (rawDate === null || rawDate === undefined) return null;

  if (typeof rawDate === 'number' && Number.isFinite(rawDate)) {
    const parsed = moment(rawDate);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
  }

  if (typeof rawDate === 'string') {
    const value = rawDate.trim();
    if (!value) return null;

    const maybeEpoch = Number(value);
    const parsed =
      Number.isFinite(maybeEpoch) && /^\d+$/.test(value)
        ? moment(maybeEpoch)
        : moment(value);

    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
  }

  return null;
}

function normalizeDroneRootUrl(root: string | null | undefined): string | null {
  if (!root || typeof root !== 'string') return null;
  const trimmed = root.trim().replace(/\/+$/, '');
  return trimmed || null;
}

// Drone tiles live under each field's rootLayerPathLink (unique per survey).
function getDroneRootLayerPath(
  field: IPolygonData | null | undefined,
): string | null {
  const entries = field?.availableDroneLayers;
  if (!entries?.length) return null;
  const sorted = [...entries].sort((a, b) => {
    const tb = Number(b.analysisDate) || 0;
    const ta = Number(a.analysisDate) || 0;
    return tb - ta;
  });
  return normalizeDroneRootUrl(sorted[0]?.rootLayerPathLink);
}

const droneLayerOptions: LayerOption[] = [
  {
    key: 'rgb',
    icon: (
      <FontAwesomeIcon
        icon={faHandHoldingSeedling}
        style={{color: '#259D64', height: 24, width: 24}}
      />
    ),
    label: 'RGB',
    valueItem: EFarmLayer.RGB,
  },
  {
    key: 'NDVI',
    icon: (
      <FontAwesomeIcon
        icon={faHandHoldingSeedling}
        style={{color: '#259D64', height: 24, width: 24}}
      />
    ),
    label: 'NDVI',
    valueItem: EFarmLayer.CHLOROPHYLL,
  },
  {
    key: 'GNDVI',
    icon: (
      <FontAwesomeIcon
        icon={faHandHoldingSeedling}
        style={{color: '#259D64', height: 24, width: 24}}
      />
    ),
    label: 'GNDVI',
    valueItem: EFarmLayer.NDVI,
  },

  {
    key: 'LCI',
    icon: (
      <FontAwesomeIcon
        icon={faDroplet}
        style={{color: '#259D64', height: 24, width: 24}}
      />
    ),
    label: 'Chlorophyll',
    valueItem: EFarmLayer.CHLOROPHYLL,
  },
];
// Our layer options
const layerOptions = [
  {
    key: 'chlorophyll',
    icon: (
      <FontAwesomeIcon
        icon={faHandHoldingSeedling}
        style={{color: '#259D64', height: 24, width: 24}}
      />
    ),
    label: 'Chlorophyll',
    valueItem: EFarmLayer.CHLOROPHYLL,
  },
  {
    key: 'ndvi',
    icon: (
      <FontAwesomeIcon
        icon={faHandHoldingSeedling}
        style={{color: '#259D64', height: 24, width: 24}}
      />
    ),
    label: 'NDVI',
    valueItem: EFarmLayer.NDVI,
  },

  {
    key: 'moisture1',
    icon: (
      <FontAwesomeIcon
        icon={faDroplet}
        style={{color: '#259D64', height: 24, width: 24}}
      />
    ),
    label: 'Moisture',
    valueItem: EFarmLayer.MOISTURE,
  },
  // {
  //   key: 'weather1',
  //   icon: (
  //     <FontAwesomeIcon
  //       icon={faHandHoldingSeedling}
  //       style={{color: '#259D64', height: 24, width: 24}}
  //     />
  //   ),
  //   label: 'Weather',
  //   valueItem: EFarmLayer.WEATHER,
  // },
];
function CustomerDashboardMapping(props: CustomerDashboardProps) {
  const {customerUserId} = props.match.params;
  const mapRef = useRef<google.maps.Map | null>(null);
  const hasAppliedInitialViewportRef = useRef(false);
  const droneTileMapTypesRef = useRef<google.maps.ImageMapType[]>([]);

  // Basic state
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [customerData, setCustomerData] = useState<IUser | null>(null);

  // Map polygons
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [successSnackbarMessage, setSuccessSnackbarMessage] = useState('');
  const [mapCoords, setMapCoords] = useState<{lat: number; lng: number}>({
    lat: -25.89674,
    lng: 30.137756,
  });
  const [polygons, setPolygons] = useState<IPolygonData[]>([]);

  // This is the new piece of state for the “clicked polygon”
  const [clickedPolygon, setClickedPolygon] = useState<IPolygonData | null>(
    null,
  );

  // NDVI data
  const [showLayers, setShowLayers] = useState<boolean>(false);

  // Layer data
  const [layerData, setLayerData] = useState<Array<ILayerResponse[]>>([]);
  // Date picking
  const [startDate, setStartDate] = useState<Moment | null>(null);
  const [endDate, setEndDate] = useState<Moment | null>(null);
  const [datesOpen, setDatesOpen] = useState<boolean>(false);

  // Add field flow
  const [drawingMode, setDrawingMode] = useState<DrawingModeType>(null);
  const [draftPolygon, setDraftPolygon] = useState<google.maps.Polygon | null>(
    null,
  );
  const [polygonIsComplete, setPolygonIsComplete] = useState<boolean>(false);
  const [addNewFieldFormOpen, setAddNewFieldFormOpen] =
    useState<boolean>(false);
  const [editFieldData, setEditFieldData] = useState<IPolygonData | null>(null);
  const [addDataPointField, setAddDataPointField] =
    useState<IPolygonData | null>(null);
  const [drawnPolygons, setDrawnPolygons] = useState<any>([]);
  const [isChoosingOnMap, setIsChoosingOnMap] = useState<boolean>(false);
  const [mapPickedLocation, setMapPickedLocation] = useState<string | null>(
    null,
  );

  // Right after your other hooks at the top:
  const drawRef = useRef<TerraDraw | null>(null);
  const [isPartialDrawing, setIsPartialDrawing] = useState<boolean>(false);

  // Layer logic
  const [selectedLayer, setSelectedLayer] = useState(layerOptions[0]);
  const [selectedDroneLayer, setSelectedDroneLayer] =
    useState<LayerOption | null>(null);
  const [isDroneLayerVisible, setIsDroneLayerVisible] =
    useState<boolean>(false);
  const [showLayerSelector, setShowLayerSelector] = useState<boolean>(false);

  const [layersLoading, setLayersLoading] = useState<boolean>(false);
  const [isMapReady, setIsMapReady] = useState(false);
  const [loadedLayerRequestKey, setLoadedLayerRequestKey] = useState<
    string | null
  >(null);
  const [resolvedLayerDate, setResolvedLayerDate] = useState<string | null>(
    null,
  );

  const [selectedPolygonId, setSelectedPolygonId] = useState<
    string | number | null
  >(null);

  const availableDateKeys = useMemo(() => {
    const keys = new Set<string>();
    const dateSourcePolygons = selectedPolygonId
      ? polygons.filter(
          (pol) => String(pol.id ?? '') === String(selectedPolygonId),
        )
      : polygons;

    dateSourcePolygons.forEach((polygon) => {
      (polygon.risks || []).forEach((risk) => {
        const dateKey =
          typeof risk === 'string'
            ? toIsoDayKey(risk)
            : toIsoDayKey(risk?.analysisDate ?? risk?.date);
        if (dateKey) keys.add(dateKey);
      });

      (polygon.availableLayers || []).forEach((item) => {
        const hasLayerUrl = (item?.layers || []).some((layer) => !!layer?.url);
        if (!hasLayerUrl) return;
        const dateKey = toIsoDayKey(item?.date);
        if (dateKey) keys.add(dateKey);
      });
    });

    return keys;
  }, [polygons, selectedPolygonId]);

  const mostRecentAvailableDate = useMemo<Moment | null>(() => {
    let latest: Moment | null = null;

    availableDateKeys.forEach((dateKey) => {
      const parsed = moment(dateKey, 'YYYY-MM-DD', true);
      if (!parsed.isValid()) return;

      if (!latest || parsed.isAfter(latest)) {
        latest = parsed;
      }
    });

    return latest;
  }, [availableDateKeys]);

  const layerFieldSignature = useMemo(
    () =>
      polygons
        .map((polygon, idx) => {
          const idPart = polygon.id ?? `idx-${idx}`;
          const pointsCount = polygon.coordinates?.length || 0;
          return `${idPart}:${pointsCount}`;
        })
        .join('|'),
    [polygons],
  );

  useEffect(() => {
    if (drawingMode === 'POLYGON') {
      setIsPartialDrawing(true);
      setPolygonIsComplete(false);
      setDraftPolygon(null);
      if (drawRef.current) {
        drawRef.current.setMode('polygon');
      }
    } else {
      if (drawRef.current) {
        drawRef.current.setMode('render');
      }
    }
  }, [drawingMode]);

  useEffect(() => {
    hasAppliedInitialViewportRef.current = false;
  }, [customerUserId]);

  useEffect(() => {
    if (!showLayerSelector && datesOpen) {
      setDatesOpen(false);
    }
  }, [datesOpen, showLayerSelector]);

  useEffect(() => {
    if (!mostRecentAvailableDate) return;

    const currentStartDateKey = startDate
      ? startDate.clone().startOf('day').format('YYYY-MM-DD')
      : null;

    if (!currentStartDateKey || !availableDateKeys.has(currentStartDateKey)) {
      setStartDate(mostRecentAvailableDate.clone().startOf('day'));
      setEndDate(null);
    }
  }, [availableDateKeys, mostRecentAvailableDate, startDate]);

  const apiTocallUserData = () => {
    apiGetUser(customerUserId)
      .then((fetchedUser) => {
        console.log('Fetched user:', fetchedUser);
        const farm = fetchedUser?.farmer?.farm;
        setCustomerData(fetchedUser);
        setMapCoords(parseLatLong(farm?.latLong));
        setPolygons(farm?.fields ?? []);
        setShowLayers(false);
        setLayerData([]);
        setLoadedLayerRequestKey(null);
        setResolvedLayerDate(null);
        setStartDate(null);
        setEndDate(null);
        setDatesOpen(false);
        hasAppliedInitialViewportRef.current = false;
      })
      .catch((err) => console.error('Error fetching user', err))
      .finally(() => setIsLoading(false));
  };
  // ===== Fetch user data
  useEffect(() => {
    setIsLoading(true);
    apiTocallUserData();
  }, [customerUserId]);

  const handleRemoveLayer = () => {
    // Turn off NDVI/overlay, so we revert to normal polygons
    setShowLayers(false);
    setResolvedLayerDate(null);
  };

  // ====== onPolygonComplete: user finishes drawing a new polygon
  const onPolygonComplete = useCallback((id: string | number) => {
    if (!drawRef.current) return;
    const feature = drawRef.current.getSnapshotFeature(id as string);
    if (feature && feature.geometry.type === 'Polygon') {
      const coords = feature.geometry.coordinates[0];
      const path = coords.map((c: any) => ({lat: c[1], lng: c[0]}));
      const tempPolygon = new google.maps.Polygon({paths: path});
      setDraftPolygon(tempPolygon);
      setPolygonIsComplete(true);
      setIsPartialDrawing(false);
    }
  }, []);

  useEffect(() => {
    if (!isMapReady || !mapRef.current) return;

    const map = mapRef.current;
    let draw: TerraDraw | null = null;

    const initDraw = () => {
      draw = new TerraDraw({
        adapter: new TerraDrawGoogleMapsAdapter({
          map,
          lib: window.google.maps,
        }),
        modes: [
          new TerraDrawRenderMode({modeName: 'render', styles: {}}),
          new TerraDrawPolygonMode(),
        ],
      });

      draw.on('finish', (id: string | number) => {
        onPolygonComplete(id);
      });

      draw.start();
      draw.setMode('render');
      drawRef.current = draw;
    };

    // Wait for the map tiles to be fully loaded before initialising TerraDraw.
    // The adapter creates a google.maps.OverlayView internally, and its DOM
    // container is only available after 'tilesloaded'.
    const listener = window.google.maps.event.addListenerOnce(
      map,
      'tilesloaded',
      initDraw,
    );

    return () => {
      window.google.maps.event.removeListener(listener);
      if (draw) {
        try {
          draw.stop();
        } catch (_) {
          // ignore cleanup errors
        }
        drawRef.current = null;
      }
    };
  }, [isMapReady, onPolygonComplete]);

  // ====== handlePolygonClick: called when an existing polygon on the map is clicked
  const handlePolygonClick = (polygonData: IPolygonData) => {
    setClickedPolygon(polygonData);
    setSelectedPolygonId(polygonData?.id ?? null); // highlight on the map
  };

  const focusMapOnField = (field: IPolygonData) => {
    const map = mapRef.current;
    if (!map || !field?.coordinates?.length) return;

    const bounds = new google.maps.LatLngBounds();
    field.coordinates.forEach((coord) => {
      bounds.extend({lat: coord.lat, lng: coord.lng});
    });

    map.fitBounds(bounds);

    google.maps.event.addListenerOnce(map, 'idle', () => {
      const zoom = map.getZoom() ?? 15;
      if (zoom > 18) map.setZoom(18);
      if (zoom < 15) map.setZoom(15);
    });
  };

  const fitMapToFields = React.useCallback((fieldsToFit: IPolygonData[]) => {
    const map = mapRef.current;
    if (!map || typeof google === 'undefined' || !google.maps) return false;

    const bounds = new google.maps.LatLngBounds();
    let hasCoordinates = false;

    fieldsToFit.forEach((field) => {
      (field.coordinates || []).forEach((coord) => {
        if (!Number.isFinite(coord.lat) || !Number.isFinite(coord.lng)) return;
        bounds.extend({lat: coord.lat, lng: coord.lng});
        hasCoordinates = true;
      });
    });

    if (!hasCoordinates) return false;

    map.fitBounds(bounds);
    google.maps.event.addListenerOnce(map, 'idle', () => {
      const zoom = map.getZoom() ?? 15;
      if (zoom > 18) map.setZoom(18);
    });

    return true;
  }, []);

  const focusMapOnLocation = (location: {lat: number; lng: number}) => {
    const map = mapRef.current;
    if (!map) return;

    map.panTo({lat: location.lat, lng: location.lng});
    map.setZoom(16);
  };

  useEffect(() => {
    if (!isMapReady || isLoading || hasAppliedInitialViewportRef.current) {
      return;
    }

    const hasFields = fitMapToFields(polygons);
    const map = mapRef.current;

    if (!hasFields && map) {
      const hasSavedFarmLocation = !!customerData?.farmer?.farm?.latLong;
      map.panTo(mapCoords);
      map.setZoom(hasSavedFarmLocation ? 15 : 6);
    }

    hasAppliedInitialViewportRef.current = true;
  }, [
    customerData?.farmer?.farm?.latLong,
    fitMapToFields,
    isLoading,
    isMapReady,
    mapCoords,
    polygons,
  ]);

  // ===== Save polygon
  const handleSavePolygon = () => {
    if (!draftPolygon) return;
    const polygonPath = draftPolygon.getPath();
    const areaInSqMeters =
      google.maps.geometry.spherical.computeArea(polygonPath);
    const areaInHectares = areaInSqMeters / 10000;
    const coords = polygonPath
      .getArray()
      .map((latLng) => ({lat: latLng.lat(), lng: latLng.lng()}));

    setDrawnPolygons({size: areaInHectares, coordinates: coords});
    setAddNewFieldFormOpen(true);
    setPolygonIsComplete(false);
  };

  // ===== Cancel polygon
  const handleCancelPolygon = () => {
    // If polygon is incomplete
    if (isPartialDrawing && !polygonIsComplete) {
      // Cancel partial shape
      if (drawRef.current) {
        drawRef.current.clear();
      }
      setIsPartialDrawing(false);
      setPolygonIsComplete(false);
      setDraftPolygon(null);
      setDrawingMode(null); // go back to idle (2-button) mode
    } else {
      // If the polygon was fully completed
      if (drawRef.current) {
        drawRef.current.clear();
      }
      setDraftPolygon(null);
      setPolygonIsComplete(false);
      setIsPartialDrawing(false);
      setDrawingMode(null);
    }
  };

  // ===== Delete polygon
  const handleDeletePolygon = () => {
    // If still half-drawn, forcibly remove it from the map, but stay in 5-button mode
    if (isPartialDrawing && !polygonIsComplete) {
      if (drawRef.current) {
        drawRef.current.clear();
      }
      // We remain in drawing mode => do not setDrawingMode(null)
    } else {
      // If the polygon was fully completed
      if (drawRef.current) {
        drawRef.current.clear();
      }
      setDraftPolygon(null);
      setPolygonIsComplete(false);
      // We do NOT exit drawing mode => user stays in 5-button layout
    }
  };

  // ===== Undo / Redo placeholders
  const handleUndo = () => {
    console.log('Undo last vertex...');
  };
  const handleRedo = () => {
    console.log('Redo last vertex...');
  };

  // ===== Layer logic
  const getSelectedAnalysisDate = (
    rangeStartDate?: Moment | null,
    rangeEndDate?: Moment | null,
  ) => {
    if (rangeStartDate && rangeEndDate) {
      const rangeStartKey = rangeStartDate
        .clone()
        .startOf('day')
        .format('YYYY-MM-DD');
      const rangeEndKey = rangeEndDate
        .clone()
        .startOf('day')
        .format('YYYY-MM-DD');
      const availableRangeDates = Array.from(availableDateKeys)
        .filter((dateKey) => dateKey >= rangeStartKey && dateKey <= rangeEndKey)
        .sort();

      if (availableRangeDates.length > 0) {
        const latestInRange =
          availableRangeDates[availableRangeDates.length - 1];
        return moment(latestInRange, 'YYYY-MM-DD').startOf('day');
      }
    }

    return (
      rangeEndDate ||
      rangeStartDate ||
      endDate ||
      startDate ||
      mostRecentAvailableDate ||
      moment()
    )
      .clone()
      .startOf('day');
  };

  const getLayerRequestKey = (
    rangeStartDate?: Moment | null,
    rangeEndDate?: Moment | null,
  ) => {
    const selectedAnalysisDate = getSelectedAnalysisDate(
      rangeStartDate,
      rangeEndDate,
    );

    return `${selectedAnalysisDate.format(
      'YYYY-MM-DD',
    )}::${layerFieldSignature}`;
  };

  const handleLayerSelect = async (key: string) => {
    const found =
      layerOptions.find((option) => option.key === key) ?? layerOptions[0];
    setSelectedLayer(found);

    const currentRequestKey = getLayerRequestKey();
    const hasDataForCurrentContext =
      layerData.length > 0 && loadedLayerRequestKey === currentRequestKey;

    if (hasDataForCurrentContext) {
      setShowLayers(true);
      return;
    }

    await getMapLayers();
  };
  const handleDroneLayerSelected = (key: string) => {
    handleRemoveLayer();
    const found =
      droneLayerOptions.find((option) => option.key === key) ??
      droneLayerOptions[0];
    setSelectedDroneLayer(found);
    setShowLayers(false);
    setIsDroneLayerVisible(true);
  };

  const getMapLayers = async (
    rangeStartDate?: Moment | null,
    rangeEndDate?: Moment | null,
  ) => {
    if (polygons.length < 1) return;
    try {
      setLayersLoading(true);
      const selectedAnalysisDate = getSelectedAnalysisDate(
        rangeStartDate,
        rangeEndDate,
      );
      const analysisDate = selectedAnalysisDate.format('YYYY-MM-DD');
      const requestKey = getLayerRequestKey(rangeStartDate, rangeEndDate);
      const mappedLayersFromState = polygons.map((polygon) => {
        const historyForDate = (polygon.availableLayers || []).find(
          (item) => toIsoDayKey(item?.date) === analysisDate,
        );

        return (historyForDate?.layers || [])
          .filter((layer) => !!layer?.url)
          .map((layer) => ({
            url: layer?.url || '',
            error: '',
            riskLevel: '',
            layer: layer?.layer as EFarmLayer,
          }));
      });
      const hasStoredHistory = mappedLayersFromState.some((layers) =>
        layers.some((layer) => !!layer.url),
      );

      if (hasStoredHistory) {
        setLayerData(mappedLayersFromState);
        setLoadedLayerRequestKey(requestKey);
        setResolvedLayerDate(analysisDate);
        setShowLayers(true);
        return;
      }

      const fieldIds = polygons
        .map((polygon) => polygon.id)
        .filter((id): id is string => typeof id === 'string' && !!id.trim());

      if (fieldIds.length < 1) {
        setLayerData(polygons.map(() => []));
        setLoadedLayerRequestKey(requestKey);
        setResolvedLayerDate(analysisDate);
        setShowLayers(true);
        return;
      }

      const historyLayers = await apiFieldLayersByIds(fieldIds, analysisDate);
      const layersByFieldId = new Map(
        historyLayers.map((item) => [String(item.fieldId), item.layers || []]),
      );
      const mappedLayers = polygons.map((polygon) =>
        polygon?.id ? layersByFieldId.get(String(polygon.id)) || [] : [],
      );

      setLayerData(mappedLayers);
      setLoadedLayerRequestKey(requestKey);
      setResolvedLayerDate(analysisDate);
      setShowLayers(true);
    } catch (e) {
      console.error('Error fetching map layers', e);
    } finally {
      setLayersLoading(false);
    }
  };

  // ===== Date logic
  const applySelectedDate = async (date: Moment | null) => {
    if (!date) return;

    const nextDate = date.clone().startOf('day');
    setStartDate(nextDate);
    setEndDate(null);

    if (showLayers) {
      await getMapLayers(nextDate, null);
    }
  };

  const handleDateChange = (date: Moment | null) => {
    void applySelectedDate(date);
  };

  const handleGoToMostRecentDate = async () => {
    if (!mostRecentAvailableDate) return;
    await applySelectedDate(mostRecentAvailableDate);
  };

  const handleCancelDates = () => {
    setDatesOpen(false);
  };
  const getlayerUrl = (
    polygonLayerData: ILayerResponse[],
    layerType: EFarmLayer,
  ): string => {
    const layerItem = polygonLayerData?.find(
      (item) => item.layer === layerType,
    );
    return layerItem?.url ?? '';
  };

  const clearDroneTileOverlays = useCallback((map: google.maps.Map) => {
    const stored = droneTileMapTypesRef.current;
    if (!stored.length) return;
    const om = map.overlayMapTypes;
    for (let i = om.getLength() - 1; i >= 0; i--) {
      const at = om.getAt(i);
      if (stored.includes(at as google.maps.ImageMapType)) {
        om.removeAt(i);
      }
    }
    droneTileMapTypesRef.current = [];
  }, []);

  const handleRemoveDroneLayer = () => {
    setIsDroneLayerVisible(false);
    const map = mapRef.current;
    if (map) {
      clearDroneTileOverlays(map);
    } else {
      droneTileMapTypesRef.current = [];
    }
  };

  const handleMapLoading = useCallback(
    (map: google.maps.Map) => {
      clearDroneTileOverlays(map);
      if (!isDroneLayerVisible) {
        return;
      }
      const activeField =
        (selectedPolygonId != null
          ? polygons.find(
              (p) => String(p.id ?? '') === String(selectedPolygonId),
            )
          : null) ?? clickedPolygon;

      let droneRoots: string[];
      if (activeField) {
        const root = getDroneRootLayerPath(activeField);
        droneRoots = root ? [root] : [];
      } else {
        const roots = new Set<string>();
        polygons.forEach((p) => {
          const r = getDroneRootLayerPath(p);
          if (r) roots.add(r);
        });
        droneRoots = Array.from(roots);
      }

      if (droneRoots.length < 1) {
        return;
      }

      const layerKey = selectedDroneLayer?.key;
      const cacheBuster = Date.now();
      droneTileMapTypesRef.current = [];
      droneRoots.forEach((droneRoot) => {
        const tileLayer = new window.google.maps.ImageMapType({
          getTileUrl: (coord, zoom) => {
            const tmsY =
              layerKey?.toLowerCase() === 'rgb'
                ? Math.pow(2, zoom) - coord.y - 1
                : Math.pow(2, zoom) - coord.y - 1;
            return `${droneRoot}/${layerKey}/${zoom}/${coord.x}/${tmsY}.png?v=${cacheBuster}`.toLowerCase();
          },
          tileSize: new google.maps.Size(256, 256),
          maxZoom: 21,
          name: 'Drone tile layer',
          opacity: 1,
        });
        droneTileMapTypesRef.current.push(tileLayer);
        map.overlayMapTypes.insertAt(0, tileLayer);
      });
    },
    [
      clearDroneTileOverlays,
      clickedPolygon,
      isDroneLayerVisible,
      polygons,
      selectedDroneLayer,
      selectedPolygonId,
    ],
  );

  useEffect(() => {
    if (mapRef.current) {
      handleMapLoading(mapRef.current);
    }
  }, [handleMapLoading]);

  const farmName = customerData?.farmer?.farm?.name || 'Farm Name';
  const companyName =
    customerData?.company?.name || customerData?.farmer?.farm?.name || '';
  const farmerName = `${
    customerData?.farmer?.name || customerData?.name || ''
  } ${customerData?.farmer?.surname || customerData?.surname || ''}`.trim();
  const farmProvince = customerData?.farmer?.farm?.province || '';

  return (
    <Box className="customer-dashboard-container-mapping">
      <Box mb={2} className="breadcrumb-row">
        <Breadcrumbs aria-label="breadcrumb">
          <Link
            to="/customers"
            style={{textDecoration: 'none', color: '#267373'}}
          >
            Farmers
          </Link>
          <Link
            to={`/customers/dashboard/${customerUserId}`}
            style={{textDecoration: 'none', color: '#267373'}}
          >
            <Typography>{farmName}</Typography>
          </Link>
          <Typography color="textPrimary" style={{fontWeight: 700}}>
            Mapping
          </Typography>
        </Breadcrumbs>
      </Box>

      <Box className="dashboard-grid">
        <Box className="map-section card-section no-padding">
          {isLoading ? (
            <Skeleton variant="rect" width="100%" height={180} />
          ) : (
            <Box className="mini-map-container">
              <LoadScript
                googleMapsApiKey={GOOGLE_API_KEY}
                libraries={['drawing', 'geometry', 'places']}
              >
                <GoogleMap
                  mapContainerStyle={{width: '100%', height: '100%'}}
                  zoom={15}
                  center={mapCoords}
                  mapTypeId="satellite"
                  onLoad={(map) => {
                    mapRef.current = map;
                    setIsMapReady(true);
                    handleMapLoading(mapRef.current);
                  }}
                  onClick={(e) => {
                    if (isChoosingOnMap && e.latLng) {
                      setMapPickedLocation(
                        `${e.latLng.lat()}, ${e.latLng.lng()}`,
                      );
                      setIsChoosingOnMap(false);
                    }
                  }}
                  options={{
                    streetViewControl: false,
                    fullscreenControl: false,
                    mapTypeControl: false,
                    zoomControl: false,
                    scaleControl: false,
                    rotateControl: false,
                    panControl: false,
                    disableDefaultUI: true,
                    mapTypeId: 'satellite',
                  }}
                >
                  {/* Render existing polygons + labels */}
                  {polygons.map((polygon: IPolygonData, index: number) => {
                    const centroid = getPolygonCentroid(polygon.coordinates);
                    const polygonCoords = polygon.coordinates || [];
                    const selectedLayerUrl = getlayerUrl(
                      layerData[index],
                      selectedLayer.valueItem,
                    );

                    // Check if it’s the selected one
                    const isSelected = polygon.id === selectedPolygonId;

                    return (
                      <React.Fragment key={polygon.id || index}>
                        {!showLayers && !isDroneLayerVisible ? (
                          <Polygon
                            paths={polygonCoords}
                            options={{
                              fillColor: isSelected ? '#2D9B73' : '#d3d3d3',
                              fillOpacity: isSelected ? 0.6 : 0.4,
                              strokeColor: isSelected ? '#013333' : 'gray',
                              strokeOpacity: isSelected ? 1.0 : 0.8,
                              strokeWeight: isSelected ? 3 : 2,
                              clickable: true,
                              editable: !isChoosingOnMap && isSelected,
                            }}
                            onClick={(e) => {
                              if (isChoosingOnMap && e.latLng) {
                                setMapPickedLocation(
                                  `${e.latLng.lat()}, ${e.latLng.lng()}`,
                                );
                                setIsChoosingOnMap(false);
                              } else if (!isChoosingOnMap) {
                                handlePolygonClick(polygon);
                              }
                            }}
                          />
                        ) : (
                          showLayers &&
                          layerData.length > 0 &&
                          selectedLayerUrl &&
                          polygonCoords.length > 2 && (
                            <OverlayImage
                              key={`${selectedLayer.valueItem}-${polygon.id}`} // ← force re-mount when layer changes
                              polygonPaths={polygonCoords}
                              url={selectedLayerUrl}
                            />
                          )
                        )}

                        {/* Always show label */}
                        <OverlayView
                          position={centroid}
                          mapPaneName={OverlayView.FLOAT_PANE}
                          getPixelPositionOffset={(width, height) => ({
                            x: -(width / 2),
                            y: -(height / 2),
                          })}
                        >
                          <div className="polygon-label">
                            <div>{polygon?.info?.fieldName || 'Unnamed'}</div>
                            <div>{polygon?.info?.produce?.name || 'Crop?'}</div>
                            <div>
                              {polygon?.info?.hectares
                                ? `${polygon?.info?.hectares.toFixed(2)} ha`
                                : '-- ha'}
                            </div>
                          </div>
                        </OverlayView>
                      </React.Fragment>
                    );
                  })}
                  {mapPickedLocation && (
                    <Marker position={parseLatLong(mapPickedLocation)} />
                  )}
                </GoogleMap>
              </LoadScript>

              {/* FieldView on top-left, pass openPolygon={clickedPolygon} */}
              <Box
                className="map-search-box"
                style={{
                  position: 'absolute',
                  top: 20,
                  left: 20,
                  zIndex: 10,
                  display: drawingMode ? 'none' : undefined,
                }}
              >
                <FieldView
                  fields={polygons}
                  farmName={farmName}
                  companyName={companyName}
                  farmerName={farmerName}
                  farmProvince={farmProvince}
                  farmId={customerData?.farmer?.farm?.id ?? 0}
                  openPolygon={clickedPolygon}
                  selectedFieldId={selectedPolygonId}
                  onSelectField={(field) => {
                    setSelectedPolygonId(field.id ?? null);
                    setClickedPolygon(field);
                    focusMapOnField(field);
                  }}
                  onFieldUpdated={() => {
                    apiTocallUserData();
                  }}
                  onEditField={(field) => {
                    setEditFieldData(field);
                  }}
                  onDeleteField={async (field) => {
                    if (!field?.id) return;
                    try {
                      await apiDeleteFarmField(field.id);
                      if (
                        selectedPolygonId != null &&
                        String(selectedPolygonId) === String(field.id)
                      ) {
                        setSelectedPolygonId(null);
                        setClickedPolygon(null);
                      }
                      apiTocallUserData();
                      setSuccessSnackbarMessage('Field deleted successfully!');
                      setShowSuccessSnackbar(true);
                    } catch (error) {
                      console.error('Failed to delete field:', error);
                      window.alert('Failed to delete field. Please try again.');
                      throw error;
                    }
                  }}
                  onSearchLocation={focusMapOnLocation}
                />
              </Box>

              {/* AddDataButtons in bottom-middle */}
              <AddDataButtons
                drawingMode={drawingMode}
                setDrawingMode={setDrawingMode}
                polygonIsComplete={polygonIsComplete}
                onUndo={handleUndo}
                onRedo={handleRedo}
                onDelete={handleDeletePolygon}
                onReset={handleCancelPolygon}
                onSave={handleSavePolygon}
                onAddData={() => {
                  // If we have a clicked or selected field, use it.
                  // Otherwise, opening the form will default to "General Farm".
                  const currentField =
                    clickedPolygon ||
                    (selectedPolygonId != null
                      ? polygons.find(
                          (p) =>
                            String(p.id ?? '') === String(selectedPolygonId),
                        )
                      : null);

                  setAddDataPointField(
                    currentField ||
                      ({
                        id: 'GENERAL',
                        info: {fieldName: 'General Farm'},
                      } as IPolygonData),
                  );
                }}
              />

              <Box className="bottom-right-box" />

              {/* 1) Add new field / Update field / Add data point form container */}
              <Box
                className="top-right-fieldform-container"
                style={{pointerEvents: 'none'}}
              >
                {editFieldData && customerData ? (
                  <Box style={{pointerEvents: 'auto'}}>
                    <UpdateFieldForm
                      fieldData={editFieldData}
                      farmId={customerData?.farmer?.farm?.id ?? 0}
                      initialBuyer="Pepsico"
                      initialFunder="ABSA"
                      onClose={() => setEditFieldData(null)}
                      onSuccess={() => {
                        setEditFieldData(null);
                        apiTocallUserData();
                        setSuccessSnackbarMessage(
                          'Field updated successfully!',
                        );
                        setShowSuccessSnackbar(true);
                      }}
                    />
                  </Box>
                ) : addDataPointField ? (
                  <Box
                    style={{
                      display: isChoosingOnMap ? 'none' : 'block',
                      pointerEvents: 'auto',
                    }}
                  >
                    <AddNewDataPointForm
                      field={addDataPointField}
                      fields={polygons}
                      farmId={customerData?.farmer?.farm?.id ?? undefined}
                      onClose={() => {
                        setAddDataPointField(null);
                        setIsChoosingOnMap(false);
                        setMapPickedLocation(null);
                      }}
                      onSuccess={() => {
                        setAddDataPointField(null);
                        setIsChoosingOnMap(false);
                        setMapPickedLocation(null);
                        setSuccessSnackbarMessage(
                          'Data point saved successfully!',
                        );
                        setShowSuccessSnackbar(true);
                      }}
                      onChooseOnMap={() => setIsChoosingOnMap(true)}
                      pickedLocation={mapPickedLocation}
                    />
                  </Box>
                ) : addNewFieldFormOpen ? (
                  <Box style={{pointerEvents: 'auto'}}>
                    <AddNewFieldForm
                      onClose={() => setAddNewFieldFormOpen(false)}
                      drawnPolygon={drawnPolygons}
                      farmId={customerData?.farmer?.farm?.id ?? 0}
                      onRemoveDraftPolygon={() => {
                        draftPolygon?.setMap(null);
                        setDraftPolygon(null);
                        apiTocallUserData();
                      }}
                    />
                  </Box>
                ) : null}
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* FAB for layers */}
      {!drawingMode && (
        <div
          className="layer-fab-button"
          onClick={() => setShowLayerSelector(!showLayerSelector)}
        >
          <FontAwesomeIcon icon={faLayerGroup} style={{fontSize: 20}} />
        </div>
      )}

      {showLayerSelector && (
        <div className="layer-selector-wrapper">
          {datesOpen && (
            <div className="layer-dates-popup">
              <MuiPickersUtilsProvider utils={MomentUtils}>
                <Paper elevation={4} className="dates-popup-paper">
                  <Typography
                    variant="subtitle1"
                    style={{fontWeight: 600, marginBottom: 8}}
                  >
                    Select a date
                  </Typography>
                  <Typography
                    variant="caption"
                    style={{display: 'block', color: '#2D9B73'}}
                  >
                    Green circles show dates with layer data.
                  </Typography>
                  {mostRecentAvailableDate && (
                    <Typography
                      variant="caption"
                      style={{display: 'block', color: '#4f4f4f'}}
                    >
                      Most recent data:{' '}
                      {mostRecentAvailableDate.format('D MMM YYYY')}
                    </Typography>
                  )}
                  {showLayers && resolvedLayerDate && (
                    <Typography
                      variant="caption"
                      style={{display: 'block', color: '#4f4f4f'}}
                    >
                      Showing stored layer for{' '}
                      {moment(resolvedLayerDate).format('D MMM YYYY')}
                    </Typography>
                  )}

                  <Calendar
                    date={startDate || moment()}
                    onChange={handleDateChange}
                    renderDay={(
                      day,
                      selectedDate,
                      isInCurrentMonth,
                      dayComponent,
                    ) => {
                      const mDay = moment(day);
                      const dayKey = mDay.format('YYYY-MM-DD');
                      const isAvailable = availableDateKeys.has(dayKey);
                      const isSelected =
                        !!startDate && mDay.isSame(startDate, 'day');

                      return React.cloneElement(dayComponent, {
                        style: {
                          ...dayComponent.props.style,
                          backgroundColor: isSelected
                            ? '#2D9B73'
                            : isAvailable
                            ? '#D4F5E6'
                            : undefined,
                          color: isSelected
                            ? '#fff'
                            : isAvailable
                            ? '#145C43'
                            : undefined,
                          borderRadius:
                            isSelected || isAvailable ? 50 : undefined,
                        },
                      });
                    }}
                  />

                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    style={{gap: 8}}
                    mt={3}
                  >
                    <Button
                      onClick={handleGoToMostRecentDate}
                      disabled={!mostRecentAvailableDate}
                      variant="outlined"
                      style={{
                        borderRadius: 30,
                        flex: 1,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Most Recent
                    </Button>
                    <Button
                      onClick={handleCancelDates}
                      variant="outlined"
                      style={{borderRadius: 30, flex: 1}}
                    >
                      Close
                    </Button>
                  </Box>
                </Paper>
              </MuiPickersUtilsProvider>
            </div>
          )}
          <LayerSelector
            selectedLayer={selectedLayer}
            onSelect={(key: string) => handleLayerSelect(key)}
            onClose={() => {
              setDatesOpen(false);
              setShowLayerSelector(false);
            }}
            layerOptions={layerOptions}
            onRemoveLayer={handleRemoveLayer}
            layersLoading={layersLoading}
            selectedDateLabel={
              resolvedLayerDate
                ? moment(resolvedLayerDate).format('D MMM YYYY')
                : startDate
                ? startDate.format('D MMM YYYY')
                : mostRecentAvailableDate
                ? mostRecentAvailableDate.format('D MMM YYYY')
                : 'No stored dates'
            }
            canViewPastLayers={availableDateKeys.size > 0}
            onViewPastLayers={() => setDatesOpen((current) => !current)}
            droneLayerOptions={droneLayerOptions}
            selectedDroneLayer={selectedDroneLayer}
            onDroneLayerSelect={handleDroneLayerSelected}
            onRemoveDroneLayer={handleRemoveDroneLayer}
          />
        </div>
      )}

      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={4000}
        onClose={() => setShowSuccessSnackbar(false)}
        message={successSnackbarMessage}
      />
    </Box>
  );
}

const mapStateToProps = (state: any) => ({
  user: state.user as ISupplierUserProfile,
});

export default connect(mapStateToProps)(CustomerDashboardMapping);
